import React from 'react';
import FlexboxContainer from '../FlexboxContainer';
import LoadingIndicator from '../LoadingIndicator';
import GenericTableHeader from './GenericTableHeader';
import GenericTableRow from './GenericTableRow';
import { emptyDataSourceMainMessage, emptyDataSourceExtraMessage } from 'constants/errorMessages';
import styled, { css } from 'styled-components';

const TableScroll = styled.div<{ maxHeight?: string | number }>`
  width: 100%;

  ${(props: any) =>
    props.maxHeight &&
    css`
      max-height: ${typeof props.maxHeight === 'number'
        ? `${props.maxHeight}px`
        : props.maxHeight};
      overflow-y: auto;
      overflow-x: auto;
    `}
`;

export interface TableColumn {
  title: string;
  field: string;
  width?: string;
  headerStyle?: any;
  cellStyle?: any;
  render?: (rowData: any) => React.ReactNode;
}

interface Props {
  data: any[];
  columns: TableColumn[];
  onRowClick?: (event: React.MouseEvent, rowData: any) => void;
  currentPage: number;
  itemsPerPage: number;
  isLoading?: boolean;
  detailPanel?: any[];
  onSortChange?: (columnIndex: number, direction: string) => void;
  sortBy?: any;
  maxHeight?: string;
  enableSelection?: boolean;
  selectedRows?: any[];
  onSelectionChange?: (selectedRows: any[]) => void;
  isRowSelectable?: (rowData: any) => boolean;
}

const GenericTable = ({
  data,
  columns,
  onRowClick,
  currentPage,
  itemsPerPage,
  isLoading,
  detailPanel,
  onSortChange,
  sortBy,
  maxHeight,
  enableSelection,
  selectedRows = [],
  onSelectionChange,
  isRowSelectable
}: Props) => {
  // Don't slice data here - let the parent component handle pagination
  // This allows for both client-side (companies) and server-side (orders) pagination
  const dataToShow = data;

  if (isLoading) {
    return <LoadingIndicator center />;
  }

  const hasDetailPanel = detailPanel && detailPanel.length > 0;

  const selectableData = isRowSelectable ? dataToShow.filter(isRowSelectable) : dataToShow;

  const isAllSelected = selectableData.length > 0 && selectableData.every(row => selectedRows.includes(row));
  const isIndeterminate = !isAllSelected && selectableData.some(row => selectedRows.includes(row));

  const handleSelectAll = () => {
    if (isAllSelected) {
      onSelectionChange?.([]);
    } else {
      onSelectionChange?.([...selectableData]);
    }
  };

  const handleSelectRow = (rowData: any) => {
    if (selectedRows.includes(rowData)) {
      onSelectionChange?.(selectedRows.filter(r => r !== rowData));
    } else {
      onSelectionChange?.([...selectedRows, rowData]);
    }
  };

  return (
    <TableScroll maxHeight={maxHeight}>
      <FlexboxContainer flexDirection="column" minWidth="max-content">
        <GenericTableHeader
          columns={columns}
          hasDetailPanel={hasDetailPanel}
          onSortChange={onSortChange}
          sortBy={sortBy}
          enableSelection={enableSelection}
          isAllSelected={isAllSelected}
          isIndeterminate={isIndeterminate}
          onSelectAll={handleSelectAll}
        />
        {dataToShow.length === 0 ? (
          <FlexboxContainer flexDirection="column" justifyContent="center" alignItems="center" height="200px" width="100%">
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '8px' }}>
                {emptyDataSourceMainMessage}
              </div>
              <div style={{ fontSize: '18px', color: '#6B7280' }}>
                {emptyDataSourceExtraMessage}
              </div>
            </div>
          </FlexboxContainer>
        ) : (
          dataToShow.map((rowData: any, index: number) => (
            <GenericTableRow
              key={rowData.id || index}
              rowData={rowData}
              columns={columns}
              onRowClick={onRowClick}
              detailPanel={detailPanel}
              enableSelection={enableSelection}
              isSelected={selectedRows.includes(rowData)}
              onSelect={handleSelectRow}
              isRowDisabled={isRowSelectable ? !isRowSelectable(rowData) : false}
            />
          ))
        )}
      </FlexboxContainer>
    </TableScroll>
  );
};

export default GenericTable;